import { useState, useRef } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { uploadRoomImage, createRoom, parseRoomImageDimensionsFromApi } from '../api/rooms';

/**
 * Start Designing Component
 * Main panel for starting a new design project
 * Features drag & drop image upload and floorplan section.
 * When user uploads an image and clicks "Let's Begin!", opens the Photorealistic page.
 */

const StartDesigning = () => {
  const {
    setCurrentView,
    setPhotorealisticImage,
    roomId,
    setRoomImageId,
    setRoomId,
    setRoomImageDimensions,
  } = useSidebar();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showResolutionNotice, setShowResolutionNotice] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadError('Invalid file type. Please upload a JPG or PNG image.');
      return;
    }

    // Validate file size (8MB)
    const maxSize = 8 * 1024 * 1024; // 8MB in bytes
    if (file.size > maxSize) {
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadError('File size must be less than 8MB.');
      return;
    }

    setUploadError(null);
    setUploadedFile(file);
  };

  const executeBeginUpload = async () => {
    if (!uploadedFile) return;
    if (!roomId) setIsCreatingRoom(true);
    setIsUploading(true);
    setUploadError(null);
    try {
      let activeRoomId = roomId;
      if (activeRoomId == null) {
        const created = await createRoom();
        activeRoomId = created.roomId ?? created.id ?? null;
        if (activeRoomId == null) throw new Error('Failed to create room');
        setRoomId(activeRoomId);
      }

      const uploadResult = await uploadRoomImage(activeRoomId, uploadedFile);
      const imageId = uploadResult.id ?? uploadResult.roomImageId ?? uploadResult.imageId;
      if (imageId != null) setRoomImageId(imageId);
      const apiDims = parseRoomImageDimensionsFromApi(uploadResult);
      if (apiDims) setRoomImageDimensions(apiDims);
      else setRoomImageDimensions(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotorealisticImage(e.target.result);
        setCurrentView('Photorealistic');
      };
      reader.readAsDataURL(uploadedFile);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload image');
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
      setIsCreatingRoom(false);
    }
  };

  const handleLetsStartClick = () => {
    if (uploadedFile) {
      setShowResolutionNotice(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleResolutionNoticeContinue = async () => {
    setShowResolutionNotice(false);
    await executeBeginUpload();
  };

  return (
    <div className="w-full">
      {showResolutionNotice && (
        <div
          className="fixed inset-0 z-[121] flex items-center justify-center p-4 bg-black/20"
          role="dialog"
          aria-modal="true"
          aria-labelledby="resolution-notice-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowResolutionNotice(false);
          }}
        >
          <div className="w-full max-w-md rounded-[16px] border border-white/40 bg-white/35 shadow-lg backdrop-blur-xl backdrop-saturate-150 p-6 flex flex-col items-center justify-center gap-4">
            <svg className="w-8 h-8" viewBox="0 0 44 44" fill="none" stroke="currentColor" aria-hidden="true">
              <path d="M22 11.7963V24.6297M22 32.2037V31.037M43 22C43 24.7578 42.4568 27.4885 41.4015 30.0364C40.3461 32.5842 38.7993 34.8992 36.8492 36.8492C34.8992 38.7993 32.5842 40.3461 30.0364 41.4015C27.4885 42.4568 24.7578 43 22 43C19.2422 43 16.5115 42.4568 13.9636 41.4015C11.4158 40.3461 9.10079 38.7993 7.15076 36.8492C5.20073 34.8992 3.65388 32.5842 2.59853 30.0364C1.54318 27.4885 1 24.7578 1 22C1 16.4305 3.21249 11.089 7.15076 7.15076C11.089 3.21249 16.4305 1 22 1C27.5695 1 32.911 3.21249 36.8492 7.15076C40.7875 11.089 43 16.4305 43 22Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="text-sm leading-relaxed text-gray-700 text-center">
              To achieve optimal image processing and accurate results, the AI system will automatically adjust
              the resolution to 1024 px. The quality of the exported image will match or exceed that of the
              uploaded image.
            </p>
            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              {/*
              <button
                type="button"
                onClick={() => setShowResolutionNotice(false)}
                className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>*/}
              <button
                type="button"
                onClick={() => void handleResolutionNoticeContinue()}
                disabled={isUploading || isCreatingRoom}
                className="flex flex-row justify-center items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-200 hover:text-white disabled:opacity-60 transition-colors"
              >
                <svg class="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                  <path d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path><path d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadError && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-error-title"
        >
          <div className="w-full max-w-md rounded-xl shadow-lg border-dashed rounded-[16px] backdrop-blur-xl backdrop-saturate-150 bg-white/30 flex flex-col items-center justify-center min-h-[300px]">
            <div className="p-4 mb-4 flex flex-col items-center justify-center">
            <svg
              className="w-14 h-14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FF0000"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* exclamation line */}
              <path d="M12 6v7" />

              {/* exclamation dot */}
              <path d="M12 17v.01" />

              {/* circle */}
              <path d="M22 12a10 10 0 11-20 0 10 10 0 0120 0z" />
            </svg>
              <p className="mt-2 text-sm text-gray-600">{uploadError}</p>
            </div>
            <div className="flex items-center justify-end gap-2 p-4">
              <button
                type="button"
                onClick={() => {
                  setUploadError(null);
                  fileInputRef.current?.click();
                }}
                className="rounded-full bg-gray-800 px-4 py-2 text-white hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M3 17v1c0 .8.3 1.6.9 2.1.5.6 1.3.9 2.1.9h12c.8 0 1.6-.3 2.1-.9.6-.5.9-1.3.9-2.1v-1" />
                  <path d="M12 16V3" />
                  <path d="M16 7l-4-4-4 4" />
                </svg>
                Re-upload
              </button>
              <button
                type="button"
                onClick={() => setUploadError(null)}
                className="rounded-full bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M15 15l-6-6M15 9l-6 6" />
                  <path d="M22 12a10 10 0 11-20 0 10 10 0 0120 0z" />
                </svg>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <h1 className="text-[24px] lao-mn text-gray-800 mb-8">Start Designing</h1>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        {/* Left Block - Drag & Drop */}
        <div
          className={`border-[4px] border-dashed rounded-[26px] p-8 transition-colors backdrop-blur-xl backdrop-saturate-150 ${
            isDragging
              ? 'border-primary-400/80 bg-primary-100/45'
              : 'border-white/50 bg-white/35 hover:border-white/70'
          } ${uploadedFile ? '' : 'cursor-pointer'}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => {
            if (isUploading || isCreatingRoom) return;
            fileInputRef.current?.click();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            if (isUploading || isCreatingRoom) return;
            fileInputRef.current?.click();
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex flex-col items-center justify-center text-center h-full min-h-[200px]">
            {uploadedFile ? (
              <>
                <div className="mb-4">
                  <svg
                    className="w-16 h-16 text-green-500 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-gray-800 mb-2">File Uploaded!</p>
                <div className="flex item-center justify-center gap-2">
                  <p className="text-sm text-gray-600 mb-4">{uploadedFile.name}</p>
                  <button
                    onClick={(e) => {
                      // Prevent parent card click from re-opening file picker.
                      e.stopPropagation();
                      setUploadedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-sm text-primary-200 hover:text-primary-200 mb-5"
                  >
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <>
                <svg
                  className="w-12 h-12 mb-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* tray */}
                  <path d="M3 17v1.5c0 1.1.4 2.1 1.2 2.8.7.7 1.7 1.2 2.8 1.2h10c1.1 0 2.1-.4 2.8-1.2.7-.7 1.2-1.7 1.2-2.8V17" />

                  {/* arrow shaft */}
                  <path d="M12 16V3" />

                  {/* arrow head */}
                  <path d="M16 7l-4-4-4 4" />
                </svg>
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  Upload or Drag and Drop image
                </p>
              </>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileInput}
              className="hidden"
            />

            {uploadedFile && (
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLetsStartClick();
                  }}
                  disabled={isUploading || isCreatingRoom || showResolutionNotice}
                  className="bg-primary-300 text-white hover:bg-primary-200 hover:text-white disabled:opacity-60 text-gray-800 py-3 px-8 rounded-full transition-colors"
                >
                  {isCreatingRoom ? 'Creating…' : isUploading ? 'Uploading…' : "Let's Start"}
                </button>
              </div>
            )}

            {/* File Info */}
            <div className="mt-6 text-center text-xs text-gray-500">
              <p>Supported Files: JPG and PNG</p>
              <p>Maximum file size: 8mb</p>
            </div>
          </div>
        </div>

        {/* Middle Block - Open Camera */}
        <div className="border-[4px] border-dashed rounded-[26px] p-8 transition-colors backdrop-blur-xl backdrop-saturate-150 border-white/50 bg-white/35 hover:border-white/70 flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <svg
              className="w-12 h-12 mx-auto mb-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {/* folder */}
              <path d="M17 3h3c.6 0 1.1.3 1.5.7.4.4.5 1 .5 1.6v12.7c0 .7-.2 1.3-.6 1.8-.4.5-.9.7-1.5.7H3.6c-.6 0-1.1-.2-1.5-.7-.4-.5-.6-1.1-.6-1.8V5.3c0-1 .2-1.5.5-1.9.3-.6.8-1 1.4-1.3.5-.2 1.2-.2 2.3-.2.3 0 .6 0 .9.1 1.3.2 1.6 1.6 3.4 1.6H17z" />

              {/* circle (preview / lens) */}
              <path d="M14.5 18a4.5 4.5 0 100-9 4.5 4.5 0 000 9z" />
            </svg>
            <p className="text-xl font-semibold text-gray-700 mb-2">Take a picture</p>
            <p className="text-md text-gray-600 opacity-70 mb-6">Capture room photo</p>
            {/*<button
              type="button"
              disabled
              className="bg-gray-200 disabled:opacity-60 text-gray-800 font-semibold py-3 px-8 rounded-lg transition-colors pointer-events-none"
            >
              Open Camera
            </button>*/}
          </div>
        </div>

        {/* Right Block - Floorplan Coming Soon 
        <div className="border-2 border-gray-300 rounded-lg p-8 bg-gray-50 flex items-center justify-center min-h-[200px] opacity-60 pointer-events-none">
          <div className="text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-xl font-semibold text-gray-600">Upload Floorplan</p>
            <p className="text-md text-gray-600 opacity-50 mt-2">Coming Soon</p>
          </div>
        </div>*/}
        
      </div>

      {/* Lorem Ipsum Text */}
      <div className="mt-8">
        <p className="text-gray-600 leading-relaxed text-center">
          Image Upload Note: Please ensure you only upload images of room interiors. Our system is currently optimized to process and analyze indoor spaces only.
        </p>
      </div>
    </div>
  );
};

export default StartDesigning;
